import { api } from "./api";
import { API_ENDPOINTS } from "../../utils/api-service";
import { isObjectRecord } from "../../utils/functions/api-errors";
import {
  toNullableStr,
  toNumberOr,
  toNumberOrNull,
  toStr,
  unwrapApiData,
} from "./points-service-helpers";
import {
  PointHealthStatus,
  PointMetrics,
  PointMetricsHealthCheck,
  PointMetricsWeek,
} from "../../models/pointMetrics";

type UnknownRecord = Record<string, unknown>;

const asRecord = (value: unknown): UnknownRecord => (isObjectRecord(value) ? value : {});

// Conteos: un valor inválido se muestra como 0. Tasas y promedios usan toNumberOrNull directo:
// un valor inválido es "no se puede calcular" (null), nunca un 0 inventado.
const toNumberOrZero = (value: unknown): number => toNumberOr(value, 0);

const isPointHealthStatus = (value: unknown): value is PointHealthStatus =>
  value === "go" || value === "warn" || value === "no_go";

const mapWeek = (raw: UnknownRecord): PointMetricsWeek => ({
  weekStart: toStr(raw.week_start),
  pointsIssued: toNumberOrZero(raw.points_issued),
  pointsRedeemed: toNumberOrZero(raw.points_redeemed),
});

const mapHealthCheck = (raw: UnknownRecord): PointMetricsHealthCheck => ({
  key: toStr(raw.key),
  value: toNumberOrZero(raw.value),
  threshold: toNumberOrZero(raw.threshold),
  status: toStr(raw.status),
});

export const mapPointMetrics = (raw: unknown): PointMetrics => {
  const row = asRecord(raw);
  const range = asRecord(row.range);
  const emission = asRecord(row.emission);
  const redemptions = asRecord(row.redemptions);
  const engagement = asRecord(row.engagement);
  const penalties = asRecord(row.penalties);
  const health = asRecord(row.health);

  return {
    tenantId: toNullableStr(row.tenant_id),
    range: { from: toStr(range.from), to: toStr(range.to) },
    emission: {
      pointsIssued: toNumberOrZero(emission.points_issued),
      pointsRedeemed: toNumberOrZero(emission.points_redeemed),
      pointsRefunded: toNumberOrZero(emission.points_refunded),
    },
    redemptions: {
      pending: toNumberOrZero(redemptions.pending),
      expired: toNumberOrZero(redemptions.expired),
      fulfilled: toNumberOrZero(redemptions.fulfilled),
      cancelled: toNumberOrZero(redemptions.cancelled),
      validatedUnder48hRate: toNumberOrNull(redemptions.validated_under_48h_rate),
      pendingValidation: toNumberOrZero(redemptions.pending_validation),
    },
    engagement: {
      usersWithBalance: toNumberOrZero(engagement.users_with_balance),
      activeUsers: toNumberOrZero(engagement.active_users),
      balanceAdoptionRate: toNumberOrNull(engagement.balance_adoption_rate),
      averageActiveStreak: toNumberOrNull(engagement.average_active_streak),
      usersWithActiveStreak: toNumberOrZero(engagement.users_with_active_streak),
    },
    penalties: {
      noShowCount: toNumberOrZero(penalties.no_show_count),
      pointsDeducted: toNumberOrZero(penalties.points_deducted),
    },
    weeklySeries: Array.isArray(row.weekly_series)
      ? row.weekly_series.filter(isObjectRecord).map(mapWeek)
      : [],
    health: {
      status: isPointHealthStatus(health.status) ? health.status : null,
      checks: Array.isArray(health.checks) ? health.checks.filter(isObjectRecord).map(mapHealthCheck) : [],
    },
  };
};

export interface PointMetricsQueryArgs {
  tenantId: string | null;
  from: string;
  to: string;
}

export const pointMetricsApi = api.injectEndpoints({
  endpoints: (build) => ({
    getPointMetrics: build.query<PointMetrics, PointMetricsQueryArgs>({
      query: ({ tenantId, from, to }) => ({
        url: API_ENDPOINTS.POINTS.METRICS,
        // Sin tenant_id el backend devuelve todas las instituciones (SUPER_ADMIN en "Todos").
        // `from`/`to` son días calendario YYYY-MM-DD, `to` inclusive (el backend los lee en hora AR).
        params: { ...(tenantId !== null ? { tenant_id: tenantId } : {}), from, to },
      }),
      transformResponse: (response: unknown) => mapPointMetrics(unwrapApiData(response)),
      providesTags: (_result, _error, { tenantId }) => [
        { type: "pointMetrics" as const, id: tenantId ?? "ALL" },
      ],
    }),
  }),
});

export const { useGetPointMetricsQuery } = pointMetricsApi;
